import { Link, useLocation } from 'react-router-dom';
import { LogOut, User, Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState, useEffect } from 'react';
import { api } from '../App';
import AppInstallBanner from './AppInstallBanner';

const Navigation = ({ user, logout }) => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [hasRanchAccess, setHasRanchAccess] = useState(false);
  const [appLogo, setAppLogo] = useState(null);
  const [appName, setAppName] = useState('');

  useEffect(() => {
    checkRanchAccess();
    fetchAppLogo();
  }, []);

  const checkRanchAccess = async () => {
    try {
      const response = await api.get('/facility/check-qualification');
      setHasRanchAccess(response.data.is_qualified);
    } catch (error) {
      console.error('Error checking ranch access:', error);
    }
  };

  const fetchAppLogo = async () => {
    try {
      const response = await api.get('/settings/logo');
      if (response.data.logo_url) {
        setAppLogo(response.data.logo_url);
      }
      if (response.data.app_name) {
        setAppName(response.data.app_name);
      }
    } catch (error) {
      console.error('Error fetching app logo:', error);
    }
  };

  const navItems = [
    { path: '/dashboard', label: 'Dashboard' },
    { path: '/learn', label: 'Learn' },
    { path: '/adopt', label: 'Adopt' },
    { path: '/virtual-corral', label: 'Virtual Corral' },
    { path: '/the-herd', label: 'The Herd' },
    { path: '/trainer', label: 'Ask a Trainer' },
    { path: '/community', label: 'Community' }
  ];

  // Add admin-only links if user is admin
  const allNavItems = user?.is_admin 
    ? [...navItems, { path: '/ranch', label: 'Ranch' }, { path: '/admin', label: 'Admin' }]
    : navItems;

  return (
    <>
      {/* App Install Banner - Above Navigation */}
      <AppInstallBanner />
      
      <nav className="border-b sticky top-0 z-50" style={{backgroundColor: '#fff', borderColor: '#6FCFD3'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center gap-3">
            {appLogo ? (
              <img 
                src={appLogo} 
                alt="Logo" 
                className="h-10 w-auto object-contain"
              />
            ) : (
              <img 
                src="https://customer-assets.emergentagent.com/job_ranch-social/artifacts/cprb8mpd_Logo%20cream.png" 
                alt="Logo" 
                className="h-12 w-auto object-contain"
              />
            )}
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {allNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className="font-medium transition-colors flex items-center gap-1"
                style={{
                  color: location.pathname === item.path ? '#D66238' : '#372F2F',
                  borderBottom: location.pathname === item.path ? '2px solid #D66238' : 'none',
                  paddingBottom: '2px'
                }}
              >
                {item.label}
                {item.badge && (
                  <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#6FCFD3', color: 'white' }}>
                    {item.badge}
                  </span>
                )}
              </Link>
            ))}
          </div>

          {/* User Menu */}
          <div className="hidden md:flex items-center gap-4">
            <div className="flex items-center gap-2">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
              ) : (
                <User className="w-8 h-8 p-1 rounded-full" style={{backgroundColor: '#6FCFD3', color: '#fff'}} />
              )}
              <span className="font-medium" style={{color: '#D66238'}}>{user?.name}</span>
            </div>
            <Button
              data-testid="logout-btn"
              onClick={logout}
              variant="outline"
              size="sm"
              className="rounded-full"
              style={{borderColor: '#D66238', color: '#D66238'}}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6" style={{color: '#D66238'}} />
            ) : (
              <Menu className="w-6 h-6" style={{color: '#D66238'}} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t" style={{borderColor: '#6FCFD3', backgroundColor: '#fff'}}>
          <div className="px-4 py-4 space-y-3">
            {allNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className="block py-2 font-medium flex items-center gap-2"
                style={{
                  color: location.pathname === item.path ? '#D66238' : '#372F2F'
                }}
              >
                {item.label}
                {item.badge && (
                  <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#6FCFD3', color: 'white' }}>
                    {item.badge}
                  </span>
                )}
              </Link>
            ))}
            <div className="pt-3 border-t" style={{borderColor: '#6FCFD3'}}>
              {/* Mobile Coins and XP Display */}
              <div className="flex items-center justify-center gap-4 mb-3 p-2 rounded-lg bg-gradient-to-r from-amber-50 to-orange-50 border-2" style={{borderColor: '#FFD700'}}>
                <div className="flex items-center gap-1.5">
                  <img src="https://customer-assets.emergentagent.com/job_virtual-corral-1/artifacts/12b5y0cy_B2C99D99-3648-4E18-8993-68DCE291F164.png" alt="Coins" className="w-5 h-5 object-contain" />
                  <span className="font-bold text-amber-700">{user?.ranch_coins || 0}</span>
                </div>
                <div className="w-px h-5 bg-gray-300"></div>
                <div className="flex items-center gap-1.5">
                  <span className="text-xl">⭐</span>
                  <span className="font-bold text-orange-600">{user?.ranch_xp || 0} XP</span>
                </div>
              </div>
              
              <div className="flex items-center gap-2 mb-3">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                ) : (
                  <User className="w-8 h-8 p-1 rounded-full" style={{backgroundColor: '#6FCFD3', color: '#fff'}} />
                )}
                <span className="font-medium" style={{color: '#D66238'}}>{user?.name}</span>
              </div>
              <Button
                onClick={logout}
                className="w-full rounded-full"
                style={{backgroundColor: '#D66238', color: '#fff'}}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      )}
    </nav>
    
    {/* Coins and XP Display - Below Nav Bar */}
    <div className="hidden md:block sticky top-16 z-40 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-end py-2">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <img src="https://customer-assets.emergentagent.com/job_virtual-corral-1/artifacts/12b5y0cy_B2C99D99-3648-4E18-8993-68DCE291F164.png" alt="Coins" className="w-5 h-5 object-contain" />
              <span className="font-bold text-amber-700">{user?.ranch_coins || 0}</span>
            </div>
            <div className="w-px h-5 bg-gray-300"></div>
            <div className="flex items-center gap-1.5">
              <span className="text-lg">⭐</span>
              <span className="font-bold text-orange-600">{user?.ranch_xp || 0} XP</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Navigation;